"use client";

import type { ReactNode } from "react";
import { Badge, Button, cn } from "@wtc/ui";
import { Clock, FileText, Star } from "lucide-react";

import type { EmailTemplateLike } from "../types";
import { getTemplateBodyPreview } from "../folder-tree";

export interface TemplateItemProps<T extends EmailTemplateLike> {
  template: T;
  level: number;
  selected?: boolean;
  onPreview: (template: T) => void;
  onApply: (template: T) => void;
  isFavorite: boolean;
  onToggleFavorite: (templateId: string) => void;
  /**
   * Optional wrapper around the row — Hub supplies its `<ContextMenu>` here so
   * right-click "Use template" / favorite keeps working, while apps without a
   * context-menu primitive (next-email-client) render the bare row. The inline
   * star button below is always present, so favorites stay reachable on touch.
   */
  wrapRow?: (template: T, row: ReactNode) => ReactNode;
}

export function TemplateItem<T extends EmailTemplateLike>({
  template,
  level,
  selected = false,
  onPreview,
  onApply,
  isFavorite,
  onToggleFavorite,
  wrapRow,
}: TemplateItemProps<T>) {
  const bodyPreview = getTemplateBodyPreview(template.body);
  const visibleTags = (template.tags ?? []).slice(0, 2);
  const hiddenTagCount = Math.max((template.tags ?? []).length - visibleTags.length, 0);

  const row = (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        className={cn(
          "w-full justify-start text-left font-normal h-auto py-3 pr-10",
          "hover:bg-accent/50 border-l-2 hover:border-primary/20",
          selected ? "bg-accent/60 border-primary/60" : "border-transparent"
        )}
        style={{ paddingLeft: `${level * 12 + 24}px` }}
        onClick={() => onPreview(template)}
        onDoubleClick={() => onApply(template)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <FileText className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-start gap-2">
              <span className="font-medium text-xs leading-4 break-words">{template.name}</span>
              {template.is_shared && (
                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 flex-shrink-0">
                  Shared
                </Badge>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground leading-4 line-clamp-2 break-words">
              <span className="font-medium text-muted-foreground/90">Subject:</span>{" "}
              {template.subject || "No subject"}
            </div>
            {bodyPreview && (
              <div className="text-[10px] text-muted-foreground/90 leading-4 line-clamp-2 break-words">
                {bodyPreview}
              </div>
            )}
            <div className="flex items-center gap-1.5 flex-wrap">
              {visibleTags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                  {tag}
                </Badge>
              ))}
              {hiddenTagCount > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                  +{hiddenTagCount}
                </Badge>
              )}
              {template.created_at && (
                <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                  <Clock className="h-2.5 w-2.5" />
                  {new Date(template.created_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </Button>

      {/* Touch-reachable favorite toggle — the context menu is desktop-only. */}
      <button
        type="button"
        aria-pressed={isFavorite}
        aria-label={isFavorite ? `Remove ${template.name} from favorites` : `Add ${template.name} to favorites`}
        title={isFavorite ? "Remove from favorites" : "Add to favorites"}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(template.id);
        }}
        className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:bg-muted"
      >
        <Star className={cn("h-3.5 w-3.5", isFavorite && "fill-amber-400 text-amber-500")} />
      </button>
    </div>
  );

  return <>{wrapRow ? wrapRow(template, row) : row}</>;
}
